import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Layout, { PageHeader, Surface, StatusPill } from "@/components/Layout";
import TablePagination from "@/components/TablePagination";
import { api } from "@/lib/api";
import { ADMIN } from "@/lib/routes";
import {
  cardholderBlonixId,
  cardholderDisplayName,
  formatCardholderContact,
} from "@/lib/cardholderContact";

const DEFAULT_LIMIT = 25;

export default function UsersPage() {
  const [items, setItems] = useState([]);
  const [merchants, setMerchants] = useState([]);
  const [merchantFilter, setMerchantFilter] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(DEFAULT_LIMIT);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/admin/merchants").then((r) => setMerchants(r.data.items));
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: String(page), limit: String(limit) });
      if (merchantFilter) params.set("merchant_id", merchantFilter);
      const r = await api.get(`/admin/users?${params}`);
      setItems(r.data.items || []);
      setTotal(r.data.total ?? 0);
      setPages(r.data.pages ?? 0);
    } finally {
      setLoading(false);
    }
  }, [merchantFilter, page, limit]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    setPage(1);
  }, [merchantFilter, limit]);

  const byId = Object.fromEntries(merchants.map((m) => [m.id, m.company_name]));

  return (
    <Layout>
      <div className="p-4 sm:p-6 lg:p-10" data-testid="users-page">
        <PageHeader title="Cardholders" subtitle="End-users created by merchants under their KYC programme." />

        <Surface className="p-3 mb-4 flex items-center gap-3">
          <span className="text-[10px] uppercase tracking-[0.05em] font-semibold text-zinc-500">Merchant</span>
          <select
            data-testid="users-merchant-filter"
            value={merchantFilter}
            onChange={(e) => setMerchantFilter(e.target.value)}
            className="h-8 text-xs px-2 border border-zinc-200 rounded-sm bg-white focus:ring-1 focus:ring-zinc-900 focus:border-zinc-900 outline-none"
          >
            <option value="">All</option>
            {merchants.map((m) => (
              <option key={m.id} value={m.id}>{m.company_name}</option>
            ))}
          </select>
          <div className="ml-auto text-xs text-zinc-500 tabular-nums">{total} cardholders</div>
        </Surface>

        <Surface>
          <div className="table-scroll">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-[0.05em] font-semibold text-zinc-500 border-b border-zinc-100">
                <th className="px-5 py-3">Cardholder ID</th>
                <th className="px-5 py-3">Name</th>
                <th className="px-5 py-3">Email</th>
                <th className="px-5 py-3">Phone</th>
                <th className="px-5 py-3">Merchant</th>
                <th className="px-5 py-3">KYC</th>
                <th className="px-5 py-3">Created</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={6} className="px-5 py-10 text-center text-xs text-zinc-500">Loading…</td>
                </tr>
              )}
              {!loading && items.map((u) => (
                <tr key={u.id} className="border-b border-zinc-50">
                  <td className="px-5 py-3 font-mono text-xs">{cardholderBlonixId(u)}</td>
                  <td className="px-5 py-3 font-medium">{cardholderDisplayName(u)}</td>
                  <td className="px-5 py-3 text-xs">{formatCardholderContact(u)}</td>
                  <td className="px-5 py-3 text-xs">{u.phone || "—"}</td>
                  <td className="px-5 py-3 text-xs">{byId[u.merchant_id] || u.merchant_id.slice(0, 8)}</td>
                  <td className="px-5 py-3">
                    <Link to={ADMIN.userKyc(u.id)} className="inline-flex items-center gap-2 hover:underline">
                      <StatusPill value={u.kyc_status} />
                    </Link>
                  </td>
                  <td className="px-5 py-3 text-xs text-zinc-500">{(u.created_at || "").slice(0, 10)}</td>
                </tr>
              ))}
              {!loading && items.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-5 py-10 text-center text-xs text-zinc-500">No cardholders yet.</td>
                </tr>
              )}
            </tbody>
          </table>
          </div>
          <TablePagination
            page={page}
            pages={pages}
            total={total}
            limit={limit}
            loading={loading}
            onPageChange={setPage}
            onLimitChange={setLimit}
          />
        </Surface>
      </div>
    </Layout>
  );
}
